import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { Intent, getIntentProps } from '../../constants/intent'
import { theme } from '../../constants/theme'
import { Button, ButtonProps } from '../Button'
import { Icon } from '../Icon'
import { Text } from '../Text'

type ToastButtonProps = {
  design: Intent
}

const TOAST_INITIAL_PROPERTIES = {
  opacity: 0,
  transform: 'translateY(-64px)',
}

const toastAppear = keyframes({
  from: TOAST_INITIAL_PROPERTIES,
  to: {
    opacity: 1,
    transform: 'translateY(0)',
  },
})

const ToastButton = styled(Button)<ToastButtonProps>((props) => {
  const { backgroundColor, color } = getIntentProps(props.design)

  return {
    backgroundColor,
    border: 'none',
    borderRadius: theme.border.radius.normal,
    color,
    cursor: 'pointer',
    fontWeight: theme.font.weight.bold,
    paddingBottom: 8,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    whiteSpace: 'nowrap',
    '&:hover': {
      filter: 'brightness(0.95)',
    },
  }
})

const ToastElement = styled.div<Omit<ToastProps, 'text'>>((props) => {
  const { backgroundColor, color } = getIntentProps(props.design)

  return {
    alignItems: 'center',
    animationDuration: `${theme.animation.duration}s`,
    animationName: toastAppear,
    animationTimingFunction: theme.animation.easing,
    backgroundColor,
    borderRadius: theme.border.radius.normal,
    boxShadow: theme.boxShadow.elevated,
    color,
    display: 'flex',
    maxWidth: 640,
    paddingLeft: 16,
    position: 'relative',
    transform: 'translateY(0)',
    transition: `opacity ${theme.animation.transition}, transform ${theme.animation.transition}`,
    pointerEvents: 'auto',
    ...(props.removed ? TOAST_INITIAL_PROPERTIES : {}),
  }
})

export type ToastProps = {
  button?: Omit<ButtonProps, 'text'> & { text: string }
  design: Intent
  onClose?: () => void
  persistent?: boolean
  removed?: boolean
  text: string
}

export const Toast = (props: ToastProps) => {
  const { text: buttonText, ...buttonProps } = props.button ?? {}
  const { iconName } = getIntentProps(props.design)

  return (
    <ToastElement design={props.design} removed={props.removed}>
      <Icon name={iconName} />
      <Text px={3} py={2}>
        {props.text}
      </Text>
      {props.button && (
        <ToastButton {...buttonProps} design={props.design} type="button">
          {buttonText}
        </ToastButton>
      )}
      {props.persistent && (
        <ToastButton
          onClick={props.onClose}
          design={props.design}
          type="button"
        >
          <Icon name="XIcon" />
        </ToastButton>
      )}
    </ToastElement>
  )
}
